import React, { useState, useEffect, useCallback } from 'react';
import {
	Box,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	TableContainer,
	Tooltip,
	Grid,
	GridItem,
	HStack,
	VStack,
	FormControl,
	Select,
	DrawerContent,
	DrawerCloseButton,
	DrawerBody,
	DrawerOverlay,
	Drawer,
	Text,
	CardBody,
	Stack,
	Heading,
	StackDivider,
	Card,
	Input,
	Spinner,
	Flex,
	useColorModeValue
} from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/hooks';
import config from '../config';
import pako from 'pako';
import UserDropdown from '../user_dropdown/user_dropdown';

const eventTypes = [
	{ id: 0, name: 'All' },
	{ id: 1, name: 'Analytics' },
	{ id: 2, name: 'UnityLog' },
];

function LogData({ userToken, selectedGameId }) {
	const [sessionList, setSessionList] = useState([]);
	const [selectedSessionId, setSelectedSessionId] = useState(null);
	const [webSocket, setWebSocket] = useState(null);
	const [isUserSpecific, setIsUserSpecific] = useState(false);
	const [userName, setUserName] = useState(null);
	const [deviceIds, setDeviceIds] = useState([]);
	const [userList, setUserList] = useState([]);
	const borderColor = useColorModeValue('gray.300', 'gray.600');

	useEffect(() => {
		const fetchUserList = async () => {
			try {
				const response = await fetch(`${config.LOGGER_ENDPOINT}/user/list`, {
					method: 'GET',
					headers: {
						Authorization: `Bearer ${userToken}`,
					},
				});
				const data = await response.json();
				setUserList(data);
			} catch (error) {
				console.error('Error fetching user list:', error);
			}
		};

		fetchUserList();
	}, [userToken]);



	const closeWebSocket = () => {
		if (webSocket) {
			webSocket.close();
		}
	};

	const connectWebSocket = useCallback(
		(userName, deviceIds) => {
			// Use hard-coded WebSocket URL provided by backend for now
			const endpoint = 'ws://localhost:8081/ws?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySUQiOiJzb21lX3VzZXJfaWRfaGVyZSIsImlzX3N0YWZmIjp0cnVlLCJleHAiOjI3MzU2ODk2MDB9.V6UY9da6dMizeyOvbAvj6rHn4ng-uTJ8VpTmkySOKd0';

			const newWebSocket = new WebSocket(endpoint);

			newWebSocket.onopen = () => {
				// Build subscribe message following new protocol (filters inside payload)
				const filters = {};
				if (isUserSpecific && userName) filters.user_name_pattern = userName;
				if (isUserSpecific && deviceIds?.length) filters.device_id_patterns = deviceIds.join(',');

				const subscribeMsg = {
					type: 'subscribe_game_sessions',
					payload: {
						filters,
						limit: 60,
						sort_by: 'timestamp',
						sort_order: 'desc',
					},
				};
				newWebSocket.send(JSON.stringify(subscribeMsg));
			};

			newWebSocket.onmessage = (event) => {
				try {
					const incoming = JSON.parse(event.data);
					if (incoming.type === 'game_session_list' && incoming.payload?.sessions) {
						setSessionList(incoming.payload.sessions);
					} else {
						console.warn('Unhandled websocket message:', incoming);
					}
				} catch (err) {
					console.error('Error parsing websocket message', err);
				}
			};

			newWebSocket.onclose = (event) => {
				console.log('WebSocket is closed:', event);
			};

			newWebSocket.onerror = (error) => {
				console.error('WebSocket error:', error);
			};

			setWebSocket(newWebSocket);
		},
		[selectedGameId, isUserSpecific, userToken, deviceIds],
	);

	// Initial connection for general sessions
	useEffect(() => {
		if (isUserSpecific) {
			// Connect for user-specific sessions if isUserSpecific is true
			closeWebSocket();
			connectWebSocket(userName, deviceIds);
		} else {
			// Connect for general sessions if isUserSpecific is false
			closeWebSocket();
			connectWebSocket();
		}
	}, [isUserSpecific, userName, deviceIds]);

	const handleListSessions = (userName, deviceIds) => {
		setUserName(userName); // Store the userName
		setDeviceIds(deviceIds);
		setIsUserSpecific(true); // Enable user-specific mode
		setSessionList([]); // Clear the session list
	};

	const handleResetSessions = async () => {
		setUserName(null); // Clear the userName
		setDeviceIds([]); // Clear the deviceIds
		setIsUserSpecific(false); // Disable user-specific mode
		setSessionList([]); // Clear user-specific session list
	};

	const SelectedSessionRow = ({ sessionId }) => (
		<Tbody key={sessionId}>
			<Tr>
				<Td colSpan={9}>
					<VStack spacing={1} align="stretch">
						<TabbedSessionInfo userToken={userToken} selectedSessionId={sessionId} />
					</VStack>
				</Td>
			</Tr>
		</Tbody>
	);

	return (
		<Box border={'2px'} borderRadius={'5px'} borderColor={borderColor} fontSize={'14px'}>
			<h2>
				<Box as="span" flex="1" textAlign="left">
					<TableContainer size={'sm'}>
						{
							<UserDropdown
								userToken={userToken}
								onListSessions={handleListSessions}
								onResetSessions={handleResetSessions}
								userList={userList}
							/>
						}
						<Table variant="simple">
							<Thead>
								<Tr>
									<Th>User</Th>
									<Th>Platform</Th>
									<Th>Device Model</Th>
									<Th w="150px" maxW="150px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
										OS
									</Th>
									<Th>Game Version</Th>
									<Th>Build Number</Th>
									<Th>Unity Version</Th>
									<Th>Session Date</Th>
								</Tr>
							</Thead>
							{sessionList.length > 0 &&
								sessionList.map((session) => (
									<React.Fragment key={session.session_id}>
										<Tbody key={`${session.session_id}-body`}>
											<Tr onClick={() => setSelectedSessionId(selectedSessionId === session.session_id ? null : session.session_id)}>
												<Td>{""}</Td>
												<Td>{session.platform}</Td>
												<Td>{session.device_model}</Td>
												<Td w="150px" maxW="150px" isTruncated>
													<Tooltip label={session.os_version} aria-label="Full OS Version">
														{session.os_version}
													</Tooltip>
												</Td>
												<Td>{session.client_version}</Td>
												<Td>{session.build_number}</Td>
												<Td>{session.engine_version}</Td>

												<Td>{convertAndFormatTimestamp(session.session_started_at)}</Td>
											</Tr>
										</Tbody>
										{selectedSessionId === session.session_id && (
											<SelectedSessionRow key={`${session.session_id}-selected`} sessionId={selectedSessionId} />
										)}
									</React.Fragment>
								))}
							{sessionList.length === 0 && isUserSpecific && (
								<Tbody>
									<Tr>
										<Td colSpan={9} textAlign="center">
											No sessions found for the selected user and device(s).
										</Td>
									</Tr>
								</Tbody>
							)}
						</Table>
					</TableContainer>
				</Box>
			</h2>
		</Box>
	);
}

function TabbedSessionInfo({ userToken, selectedSessionId }) {
	const [eventType, setEventType] = useState('All');
	const [filterString, setFilterString] = useState('');
	const [sessionLogList, setSessionLogList] = useState([]);

	useEffect(() => {
		let retries = 0;
		let webSocket;

		const connectWebSocket = () => {
			// Connect using new authentication query parameter
			const endpoint = 'ws://localhost:8081/ws?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySUQiOiJzb21lX3VzZXJfaWRfaGVyZSIsImlzX3N0YWZmIjp0cnVlLCJleHAiOjI3MzU2ODk2MDB9.V6UY9da6dMizeyOvbAvj6rHn4ng-uTJ8VpTmkySOKd0';
			webSocket = new WebSocket(endpoint);
			//webSocket = new WebSocket(`${config.WEBSOCKET_ENDPOINT}?token=${userToken}`);

			webSocket.onopen = () => {
				retries = 0; // Reset retries after a successful connection
				if (webSocket.readyState === WebSocket.OPEN) {
					const subscribeMsg = {
						type: 'subscribe_log_stream',
						payload: { session_id: selectedSessionId },
					};
					webSocket.send(JSON.stringify(subscribeMsg));
				}
			};

			webSocket.onmessage = async (event) => {
				let data;
				if (event.data instanceof Blob) {
					const arrayBuffer = await event.data.arrayBuffer();
					const uint8Array = new Uint8Array(arrayBuffer);
					data = pako.inflate(uint8Array, { to: 'string' });
				} else {
					data = event.data;
				}

				let parsed;
				try {
					parsed = JSON.parse(data);
				} catch (error) {
					console.error('Error parsing incoming data:', error);
					return;
				}

				// Unwrap according to new protocol
				let incomingLogList = [];
				if (parsed.type === 'log_chunk' && parsed.payload?.logs) {
					incomingLogList = parsed.payload.logs;
				} else if (parsed.type === 'log_event' && parsed.payload?.log) {
					incomingLogList = [parsed.payload.log];
				} else {
					console.warn('Unhandled websocket message:', parsed);
					return;
				}

				setSessionLogList((prevVariables) => {
					const prevArray = Array.isArray(prevVariables) ? prevVariables : [];
					const newLogs = incomingLogList.filter(
						(incomingLog) => !prevArray.some((existingLog) => existingLog.EventNumber === incomingLog.EventNumber),
					);
					return [...newLogs, ...prevArray];
				});
			};

			webSocket.onclose = (event) => {
				console.log('WebSocket is closed:', event);
				// Try to reconnect if the connection was closed abnormally
				if (event.wasClean === false && retries < 5) {
					retries++;
					console.log(`Reconnecting... Attempt #${retries}`);
					setTimeout(connectWebSocket, retries * 1000); // Exponential backoff
				}
			};

			webSocket.onerror = (error) => {
				console.error('WebSocket error:', error);
			};
		};

		connectWebSocket();

		return () => {
			if (webSocket.readyState === WebSocket.OPEN || webSocket.readyState === WebSocket.CONNECTING) {
				webSocket.close();
			}
		};
	}, [selectedSessionId]);

	if (sessionLogList.length === 0) {
		return (
			<Flex justify="center" align="center" minH="100vh">
				<Spinner />
			</Flex>
		);
	}

	return (
		<>
			<VStack spacing={1} align="stretch">
				<HStack spacing={'10px'}>
					<FormControl paddingTop={'10px'} paddingBottom={'10px'} paddingLeft={'10px'} paddingRight={'10px'}>
						<Select onChange={(selected_event_type) => setEventType(selected_event_type.target.value)}>
							{eventTypes.map((event) => (
								<option key={event.id} value={event.name}>
									{event.name}
								</option>
							))}
						</Select>
					</FormControl>
					<Input placeholder="Basic usage" onChange={(filter_text) => setFilterString(filter_text.target.value)} />
				</HStack>
				<SessionLogs eventType={eventType} filterString={filterString} sessionLogList={sessionLogList} />
			</VStack>
		</>
	);
}

function SessionLogs({ eventType, filterString, sessionLogList }) {
	const [filteredGameData, setFilteredGameData] = useState(null);
	const [selectedLogInfo, setSelectedLogInfo] = useState(null);
	const { isOpen, onOpen, onClose } = useDisclosure();

	useEffect(() => {
		setFilteredGameData(sessionLogList);
		filterGameData(sessionLogList, filterString);
	}, [sessionLogList, eventType, filterString]);

	function filterGameData(sessionLogList, filterString) {
		filterString = filterString.toLowerCase();
		let result = sessionLogList;
		if (eventType !== 'All') {
			if (eventType === 'Analytics') {
				result = result.filter((field) => PrintEventName(field) !== 'Log');
			} else {
				result = result.filter((field) => PrintEventName(field) === 'Log');
			}
		}

		if (filteredGameData && filterString.length > 0) {
			let tokens = filterString.split(' ');
			for (let i = 0; i < tokens.length; i++) {
				let token = tokens[i];
				if (token === '-') return;
				if (token.includes('-')) {
					token = token.replace('-', '');
					result = result.filter((field) => !JSON.stringify(field).toLowerCase().includes(token));
				} else {
					result = result.filter((field) => JSON.stringify(field).toLowerCase().includes(tokens[i]));
				}
			}
		}
		setFilteredGameData(result);
	}

	function truncateString(str, num) {
		if (str === null) return '';
		if (str.length <= num) {
			return str;
		}
		return str.slice(0, num) + '...';
	}

	return (
		<>
			<TableContainer>
				<OpenDrawer isOpen={isOpen} onClose={onClose} selectedLogInfo={selectedLogInfo} />
				<Table variant="simple" size="sm" border={'0px'}>
					<Thead>
						<Tr>
							<Th width={'15%'}>Event Name</Th>
							<Th width={'10%'}>Log Type</Th>
							<Th width={'60%'}>Value</Th>
							<Th width={'15%'}>Timestamp</Th>
						</Tr>
					</Thead>
					<Tbody>
						{filteredGameData &&
							filteredGameData.map((field, i) => (
								<Tr
									key={field.Timestamp || i}
									onClick={() => {
										onOpen();
										setSelectedLogInfo(field);
									}}
								>
									<Td>{PrintEventName(field)}</Td>
									<Td>{PrintLogType(field)}</Td>
									<Td>{truncateString(field.LogString, 120)}</Td>
									<Td>{convertAndFormatTimestamp(field.Timestamp)}</Td>
								</Tr>
							))}
					</Tbody>
				</Table>
			</TableContainer>
		</>
	);
}

function OpenDrawer({ isOpen, onClose, selectedLogInfo }) {
	const renderLogInfo = (info) => {
		let elements = [];
		Object.entries(info).forEach(([key, value]) => {
			if (key !== '_id' && value !== null && value.length !== 0) {
				if (typeof value === 'object' && Object.keys(value).length > 0) {
					elements.push(...renderLogInfo(value));
				} else {
					elements.push(
						<Box key={key}>
							<Heading size="xs" textTransform="uppercase">
								{key}
							</Heading>
							<Text pt="2" fontSize="sm" whiteSpace="pre-line">
								{value}
							</Text>
						</Box>,
					);
				}
			}
		});
		return elements;
	};

	return (
		<>
			<Drawer onClose={onClose} isOpen={isOpen} size={'md'}>
				<DrawerOverlay />
				<DrawerContent>
					<DrawerCloseButton />
					<DrawerBody width={'95%'}>
						{selectedLogInfo && (
							<Card>
								<CardBody>
									<Stack divider={<StackDivider />} spacing="4">
										{renderLogInfo(selectedLogInfo)}
									</Stack>
								</CardBody>
							</Card>
						)}
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		</>
	);
}

function convertAndFormatTimestamp(isoDateString) {
	// Create a Date object from the ISO 8601 date string
	const date = new Date(isoDateString);

	// Add 3 hours to adjust for GMT+3 timezone
	date.setUTCHours(date.getUTCHours() + 3);

	// Extract the individual date components (using UTC methods to avoid local timezone interference if date is UTC)
	const day = String(date.getUTCDate()).padStart(2, '0');
	const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-indexed
	const year = String(date.getUTCFullYear()).slice(-2);
	const hours = String(date.getUTCHours()).padStart(2, '0');
	const minutes = String(date.getUTCMinutes()).padStart(2, '0');
	const seconds = String(date.getUTCSeconds()).padStart(2, '0');
	const milliseconds = String(date.getUTCMilliseconds()).padStart(3, '0'); // milliseconds are often 3 digits

	// Construct the final formatted string
	const finalFormattedDate = `${hours}:${minutes}:${seconds}:${milliseconds} - ${day}/${month}/${year}`;

	return finalFormattedDate;
}

function PrintEventName(eventData) {
	if (eventData.EventName === null) {
		return 'Log';
	}
	return eventData.EventName;
}

function PrintLogType(eventData) {
	if (eventData.EventName === null) {
		if (eventData.LogType === 'Log') return 'Log';
		return eventData.LogType.toLowerCase();
	}
	return 'Event';
}

function LogView({ userToken, selectedGameId }) {
	return (
		<Grid
			height={'300px'}
			width={'100%'}
			templateRows="repeat(4, 1fr)"
			templateColumns="repeat(5, 1fr)"
			gap={4}
			paddingLeft={'12px'}
			paddingRight={'10px'}
		>
			<GridItem colSpan={6} paddingTop={'10px'}></GridItem>

			<GridItem paddingLeft={'5px'} paddingRight={'5px'} colSpan={6}>
				{userToken && selectedGameId && <LogData userToken={userToken} selectedGameId={selectedGameId} />}
			</GridItem>
		</Grid>
	);
}

export default LogView;
